/*
  Итог загрузки выгрузки: что записано и что требует внимания - §3.3.2.

  Вынесено из ImportPage 08.09.2026, когда страница вместе с шагом
  сопоставления перевалила за норму в 250 строк. Граница простая: страница
  ведёт человека от файла к записи, этот блок объясняет, чем всё кончилось.

  Замечание и незаписанная строка - разные вещи. Счётчик отклонённых считает
  только вторые, поэтому одна цифра на обе (как было до осмотра 08.09.2026)
  читалась как «требует внимания 0» над таблицей из трёх замечаний.
*/

import { Tag } from './Common.jsx'
import Disclosure from './Disclosure.jsx'
import PageBar from './PageBar.jsx'
import usePaging from './usePaging.js'
import UploadEnd from './upload/UploadEnd.jsx'

/*
  🔴 С 19.10.2026 остановки сторожем объёма нет (решение владельца): загрузка
  пишется всегда, и кнопка «Объём ожидаем, загрузить», стоявшая здесь с
  08.09.2026, ушла вместе с остановкой. Под сомнением сторожа только погашение
  пропавших месяцев отчёта 1С - его решение стоит в сводке выгрузки 1С.
*/
/*
  С 20.10.2026 удачный конец - блок «Готово» (`UploadEnd`, вариант А макета):
  цифры в ряд, время записи и следующие шаги. Строки с причиной - под ним,
  свёрнутыми: их смотрят, когда они есть, а не каждый раз.
*/
export default function ImportOutcome({ outcome, accounts = null, seconds = null, onAgain = null }) {
  const failed = outcome.status === 'failed'
  const warned = outcome.issues.filter((issue) => issue.severity !== 'blocking').length
  const byHand = outcome.kept_by_hand ?? 0
  const byOnec = outcome.kept_by_onec ?? 0
  const kept = byHand + byOnec

  return (
    <section className="section">
      {failed ? (
        <div className="notice notice--warn">
          <div className="notice__what">{outcome.error_message}</div>
          <div className="notice__do">{advice({ failed, outcome, warned })}</div>
        </div>
      ) : (
        <UploadEnd
          title="Готово: начисления записаны в портфель"
          facts={[
            ['Проверено строк', outcome.rows_total],
            ['Записано', outcome.rows_accepted - kept],
            ['Не записано', outcome.rows_rejected],
            ['Оставлено без изменений', kept || null],
            ['С замечаниями', warned || null],
            ['Счетов', accounts],
          ]}
          advice={advice({ failed, outcome, warned })}
          seconds={seconds}
          onAgain={onAgain}
        />
      )}

      {!failed && kept > 0 ? <KeptNotice byHand={byHand} byOnec={byOnec} /> : null}

      {outcome.issues.length > 0 ? (
        <Disclosure
          title={`Строки с замечаниями: ${outcome.issues.length}`}
          counter="с причиной по каждой"
        >
          <Issues issues={outcome.issues} />
        </Disclosure>
      ) : null}
    </section>
  )
}

/*
  Старшинство источников (решение владельца 23.10.2026): ручной ввод главнее 1С, 1С главнее
  файла. Строки файла, которые из-за этого не записаны, называются отдельно: «принято 600»
  при записанных 200 читалось бы как успех, которого нет.
*/
function KeptNotice({ byHand, byOnec }) {
  return (
    <div className="notice notice--warn">
      {byHand > 0 ? (
        <div className="notice__what">
          Не записано строк: {byHand}. Их внесли или оплатили руками - файл ручной ввод не заменяет.
        </div>
      ) : null}
      {byOnec > 0 ? (
        <div className="notice__what">
          Не записано строк: {byOnec}. По ним есть данные из 1С - файл их не заменяет.
        </div>
      ) : null}
      <div className="notice__do">
        {byOnec > 0
          ? 'Ошибку в данных 1С исправьте руками на карточке счёта: ручной ввод главнее.'
          : 'Такие строки правятся на карточке счёта, во вкладках «Начисления» и «Оплаты».'}
      </div>
    </div>
  )
}

function Issues({ issues }) {
  // 21.10.2026: замечаний после большой выгрузки бывают тысячи - общая панель страниц.
  const { rows, bar } = usePaging(issues)
  return (
    <>
      <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th className="table__num">Строка</th>
              <th>Счёт</th>
              <th>Что не так</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((issue, index) => (
              // Ключ с номером строки файла: список только на чтение, из
              // середины ничего не удаляется, своего идентификатора у строки
              // нет. Номер строки не уникален (одна строка даёт несколько
              // замечаний), поэтому к нему добавлен порядковый номер.
              <tr key={`${issue.row_number}-${index}`}>
                <td className="table__num" data-label="Строка">
                  {issue.row_number}
                </td>
                <td data-label="Счёт">{issue.account_number ?? '-'}</td>
                <td className="table__block" data-label="Что не так">
                  <Tag kind={issue.severity === 'blocking' ? 'burn' : 'warn'}>
                    {issue.severity === 'blocking' ? 'не записана' : 'записана с замечанием'}
                  </Tag>
                  <div>{issue.explanation}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PageBar {...bar} what="замечаний" />
    </>
  )
}

function advice({ failed, outcome, warned }) {
  // Совет обязан отвечать на то, что человек видит в таблице ниже (§4.5.6).
  // Общая фраза «они не записаны» была неверна для замечаний: такие строки
  // как раз записаны, и предлагать исправлять их в выгрузке не за чем.
  if (failed) return 'Проверьте сопоставление колонок выше или исправьте файл и загрузите заново.'
  if (outcome.rows_rejected > 0) {
    return 'Незаписанные строки перечислены ниже с причиной: исправьте их в выгрузке и загрузите файл заново.'
  }
  if (warned > 0) {
    return 'Строки с замечаниями записаны. Посмотрите, чего в них не хватает: без телефона счёт недоступен для обзвона.'
  }
  return 'Портфель обновлён - откройте реестр, чтобы увидеть должников и сроки давности.'
}
